import { Box, Button, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import CustomSwitch from '../common/CustomSwitch'
import DataLoadingSpinner from '../common/DataLoadingSpinner'
import { Icons, leadingZeroFormatter } from '../../utils/utilities'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

const AuditPageStatsFilterBar = ( { statsToggle = false, viewToggle = true, handleShowFacultyWithZeroResourceChange, showZeroResourcesSwitch = true, resource, showzeroassignmentsfaculties = false, listResourceBtnLabel = "View", loadingZeroQuizzesFaculties, title, customActions = [] } ) => {

    const [showMonthPicker, setShowMonthPicker] = useState( false )

    const [searchParams, setSearchParams] = useSearchParams( { month: "", view: "tabular", stats: "faculty" } )

    const month = searchParams.get( 'month' ) || ""
    const view = searchParams.get( 'view' ) || "tabular"
    const stats = searchParams.get( 'stats' ) || "faculty"

    const { palette, layout } = useTheme()
    const navigate = useNavigate()

    const anchorRef = useRef()

    const setSearchParamValue = ( key, val ) => {
        setSearchParams( prev => {
            prev.set( key, val )
            return prev
        }, { replace: true } )
    }

    const handleFilterChange = ( e, newVal ) => {
        if ( newVal === '' || newVal === 'last30days' ) {
            setSearchParamValue( 'month', newVal )
        }
    }

    const handleMonthChange = newDate => {
        if ( newDate && newDate.isValid() ) {
            const date = new Date( newDate )
            setSearchParamValue( 'month', `${date.getFullYear()}-${leadingZeroFormatter.format( date.getMonth() + 1 )}` )
            setShowMonthPicker( false )
        }
    }

    return (
        <Box flexWrap="wrap" display="flex" gap={layout.cardsGap} alignItems={"flex-end"} justifyContent="space-between" >
            <Box>
                <Typography variant="h1" fontWeight="600" component="h2" fontSize="14px">{title}</Typography>
                {showZeroResourcesSwitch && month === "" && view === 'tabular' && <Box display="flex" alignItems="center" gap="10px">
                    <Typography color="textSecondary" fontSize="12px" variant='subtitle2'>Show faculty with zero {resource || "resources"}</Typography>
                    <CustomSwitch checked={showzeroassignmentsfaculties} onChange={handleShowFacultyWithZeroResourceChange} />
                    {loadingZeroQuizzesFaculties && <DataLoadingSpinner size="12px" padding='0' />}
                </Box>}
            </Box>
            <Box position="relative" flexWrap="wrap" display="flex" gap={layout.cardsGap} >

                {statsToggle && <ToggleButtonGroup size='small' value={stats} exclusive onChange={( e, newVal ) => newVal && setSearchParamValue( 'stats', newVal )} >
                    <Tooltip disableInteractive title="View faculty stats">
                        <ToggleButton selected={stats === "faculty"} value='faculty' sx={{ textTransform: "capitalize", padding: "4.5px 10px", gap: "10px" }} size="small">
                            {Icons.default.SupervisedUserCircle} Faculty
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip disableInteractive title="View Student Stats">
                        <ToggleButton selected={stats === "student"} sx={{ textTransform: "capitalize", padding: "4.5px 10px", gap: "10px" }} value='student' size="small">
                            {Icons.default.Group} Student
                        </ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>}

                {viewToggle && <ToggleButtonGroup size='small' value={view} exclusive onChange={( e, newVal ) => newVal && setSearchParamValue( 'view', newVal )} >
                    <Tooltip disableInteractive title="Tabular view">
                        <ToggleButton value='tabular' sx={{ fontSize: "18px", padding: "6px", '&.Mui-selected': { background: palette.primary.light, color: "white", '&:hover': { background: palette.primary.light } } }} size="small">
                            {Icons.default.ViewListIcon}
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip disableInteractive title="Graphical view">
                        <ToggleButton sx={{ fontSize: "18px", padding: "6px", '&.Mui-selected': { background: palette.primary.light, color: "white", '&:hover': { background: palette.primary.light } } }} value='graphical' size="small">
                            {Icons.default.AnalyticsIcon}
                        </ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>}

                <ToggleButtonGroup size='small' onChange={handleFilterChange} exclusive value={month || ""}>
                    {view === 'tabular' && <ToggleButton size='small' sx={{ textTransform: "capitalize", padding: "4.5px 10px" }} value="">All</ToggleButton>}
                    <ToggleButton size='small' sx={{ textTransform: "capitalize", padding: "4.5px 10px" }} value="last30days">Last 30 days</ToggleButton>
                    <ToggleButton
                        ref={anchorRef}
                        size="small"
                        sx={{
                            textTransform: "capitalize",
                            padding: "4.5px 10px",
                            cursor: "pointer"
                        }}
                        value="-"
                        selected={Boolean( month.match( /^(\d{4}-\d{2})$/ ) )}
                        onClick={() => setShowMonthPicker( true )}   // <-- OPEN PICKER
                    >
                        {month && month !== 'last30days' ? month : "Select month"}
                    </ToggleButton>
                </ToggleButtonGroup>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                        open={showMonthPicker}
                        onClose={() => setShowMonthPicker( false )}
                        views={["year", "month"]}
                        onMonthChange={handleMonthChange}
                        slotProps={{
                            textField: {
                                component: "div",
                                sx: {
                                    display: "none"      // <-- COMPLETELY HIDE INPUT
                                }
                            },
                            popper: {
                                anchorEl: anchorRef.current, // ⬅ CUSTOM ANCHOR!
                                placement: "bottom-start"
                            }
                        }}
                        slots={{
                            openPickerButton: null  // <-- HIDE CALENDAR ICON BUTTON
                        }}
                        sx={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0,
                            pointerEvents: "none"   // <-- SO IT DOESN’T BLOCK CLICKS
                        }}
                    />
                </LocalizationProvider>

                <Button size="small" onClick={() => navigate( `list` )} variant="contained" disableElevation color='secondary' >{listResourceBtnLabel}</Button>

                {customActions && customActions.length > 0 && customActions.map( ( action, index ) => {
                    const { onClick, buttonStartIcon, visible, buttonEndIcon, buttonVariant = "contained", buttonColor = "secondary", label, ...rest } = action
                    if ( onClick && typeof onClick === 'function' && visible )
                        return (
                            <Button key={index} size="small" onClick={onClick} startIcon={buttonStartIcon} endIcon={buttonEndIcon} variant={buttonVariant} disableElevation color={buttonColor} {...rest} >{label}</Button>
                        )

                    return null

                } )}

            </Box>
        </Box >
    )
}

export default AuditPageStatsFilterBar
